import { ROLE } from "providers/Auth";
import { AGENT_AUTHOR_ROLE } from "./constants";
import type { UnderwritingReportKey } from "api/underwriting-agents/types";
import {
  ApplicantType,
  ApplicationStatus,
  CriteriaRentType,
  Money,
  ReverifyRequest,
  ScreeningStatus,
  OpenReverificationRequest
} from "shared/types";

export type ActivityItem =
  | ApplicationNote
  | ApplicationActivity
  | WorkflowMessageSentActivity
  | IncomeActivity
  | ReportRunManuallyActivity
  | VerifiedIncomeAdjustedActivity
  | AdjustedApplicationRentActivity
  | AdjustedApplicantFeeActivity
  | ReverifyCompletedActivity
  | ReverifyCanceledActivity
  | ReverifyRequestedActivity
  | IncomeReverifyRequestCompletedActivity
  | IncomeReverifyRequestCanceledActivity
  | IncomeReverifyRequestCreatedActivity
  | IncomeReverifyRequestFailedActivity
  | IdvDataUpdatedActivity
  | DiscountActivity
  | RoleChangedActivity
  | NameChangedActivity
  | EmailChangedActivity
  | SplitApplicationActivity
  | VerificationTemplateOverriddenActivity
  | VerificationTemplateChangedActivity
  | TemplateRouteAnsweredActivity
  | ApplicationMergedActivity
  | ApplicationMovedActivity
  | ApplicationAssignedActivity
  | ApplicationStageChangedActivity
  | FeeRefundedActivity
  | DesiredMoveInDateUpdatedActivity
  | ReportRerunActivity
  | CriminalStatewideSearchActivity
  | CombinedIncomeRerunRequestedActivity
  | CombinedIncomeRerunCanceledActivity
  | CombinedIncomeRerunCompletedActivity
  | NewIncomeRequestCreatedActivity
  | NewIncomeRequestCanceledActivity
  | NewIncomeRequestCompletedActivity
  | HousingAssistanceAdjustedActivity
  | AgentEscalationDetectedActivity
  | AgentActionTakenActivity
  | AgentEscalatedToSupportActivity
  | AgentEscalationReviewedActivity
  | UnderwritingAgentCheckCompletedActivity
  | DesiredLeaseTermUpdatedActivity
  | PrimaryApplicantChangedActivity
  | ItemizedReceiptSentActivity
  | IncomeOverrideActivity
  | PremiumRentalVerificationSurveyActivity
  | EmployerVerificationSurveyActivity
  | IncomeItemsAssignedActivity
  | UndoDecisionEmailSentActivity
  | DecisionUndoneActivity
  | ManualApplicationStartedActivity
  | DocumentRequestedActivity
  | DocumentRequestCanceledActivity
  | AdditionalDocumentUploadedActivity
  | RentalHistoryAddressAddedActivity
  | IncomeAddedManuallyActivity
  | CancelManuallyAddedIncomeActivity
  | ScreeningStatusChangedActivity
  | LiveViewActivity;

export interface ApplicationNote {
  author?: Author;
  activity_type: "note";
  details: string;
  created_at: string;
  updated_at?: string;
  note_id: string;
  rich_text?: {
    document_html: string;
    document_text: string;
    notifications?: {
      id: number;
      author: Author;
      read_at: string | null;
      kind: string;
      user: {
        name: string;
        email: string;
        id: string;
      };
      metadata?: {
        is_mailed?: boolean;
      };
    }[];
  } | null;
}

export type Contact = {
  email: string;
  last_name: string;
  first_name: string;
  middle_name?: string;
  // Is actually not preset, but TS is happy
  role?: ApplicantType;
  is_primary?: boolean;
};
export type RepeatApplication = {
  contact: {
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
  };
  application_id: number;
  unit_name: string | null;
  property_name: string | null;
  unit_address: string | null;
};
type IdvDataUpdatedActivityDetails = {
  old: string;
  new: string;
};

interface IncomeReverifyRequestActivityBase {
  note_id: null;
  author: Author;
  details: {
    reverify_request: ReverifyRequest;
    contact?: Contact;
    income?: {
      id: number;
      name: string;
    };
    created_at: string;
    updated_at?: string;
  };
  updated_at: string;
  created_at: string;
}
export interface IncomeReverifyRequestCompletedActivity extends IncomeReverifyRequestActivityBase {
  activity_type: "income_reverify_request_completed";
}
export interface IncomeReverifyRequestCanceledActivity extends IncomeReverifyRequestActivityBase {
  activity_type: "income_reverify_request_cancelled";
}
export interface IncomeReverifyRequestCreatedActivity extends IncomeReverifyRequestActivityBase {
  activity_type: "income_reverify_request_created";
}
export interface IncomeReverifyRequestFailedActivity extends IncomeReverifyRequestActivityBase {
  activity_type: "income_reverify_request_failed";
}

export interface ReverifyRequestedActivity {
  verification_attempt_id?: number;
  note_id: null;
  author: Author;
  activity_type: "income_reverify";
  details: {
    income: {
      id: number;
      name: string;
    };
    contact: Contact;
    income_source: {
      id: number;
    };
  };
  created_at: string;
  updated_at?: string;
}

export interface ReverifyCanceledActivity extends Omit<
  ReverifyRequestedActivity,
  "activity_type"
> {
  activity_type: "income_reverify_canceled";
}

export interface ReverifyCompletedActivity {
  note_id: null;
  author: null; // actually it's {} but it's expected to be empty. Since TS treats {} as any I am putting null here
  activity_type: "income_reverify_completed";
  details: {
    income: {
      id: number;
      name: string;
    };
    contact: Contact;
    // FLoats
    current_net_monthly?: number;
    previous_net_monthly?: number;
    current_gross_monthly?: number;
    previous_gross_monthly?: number;
  };
  created_at: string;
  updated_at?: string;
}

export interface IdvDataUpdatedActivity {
  author?: Author;
  activity_type: "idv_data_updated";
  details: {
    first_name?: IdvDataUpdatedActivityDetails;
    last_name?: IdvDataUpdatedActivityDetails;
    middle_name?: IdvDataUpdatedActivityDetails;
    address1?: IdvDataUpdatedActivityDetails;
    address2?: IdvDataUpdatedActivityDetails;
    city?: IdvDataUpdatedActivityDetails;
    state?: IdvDataUpdatedActivityDetails;
    zip?: IdvDataUpdatedActivityDetails;
    country?: IdvDataUpdatedActivityDetails;
    dob?: IdvDataUpdatedActivityDetails;
    ssn?: IdvDataUpdatedActivityDetails;
    phone?: IdvDataUpdatedActivityDetails;
    contact?: Contact;
  };
  note_id: string;
  created_at: string;
  updated_at?: string;
}

export interface IncomeActivity {
  author?: Author;
  activity_type: "verification_override" | "income_override";
  created_at: string;
  note_id?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    original_income: {
      net_monthly_income: number;
      gross_monthly_income: number;
    };
    overridden_income: {
      overridden_net_monthly_income: number;
      overridden_gross_monthly_income: number;
    };
  };
}

export interface DiscountActivity {
  author?: Author;
  activity_type: "discount_applied" | "discount_removed";
  created_at: string;
  updated_at?: string;
  details: {
    contact?: Contact;
    discount_code: string;
    discount_amount: Money;
  };
}
interface ReportRunManuallyActivity {
  author?: Author;
  activity_type: "report_run_manually" | "credit_report_run_after_ssn_added";
  created_at: string;
  updated_at?: string;
  details?: {
    contact?: {
      first_name: string;
      middle_name?: string;
      last_name: string;
      email?: string;
    };
    verification_title: string;
  };
}

export interface VerifiedIncomeAdjustedActivity {
  author?: Author;
  activity_type: "verified_income_adjusted";
  created_at: string;
  updated_at?: string;
  details: {
    contact: {
      first_name: string;
      middle_name?: string;
      last_name: string;
      email: string;
    };
    original_income: {
      gross_monthly_income: Money;
      net_monthly_income: Money;
    };
    adjusted_income: {
      gross_monthly_income: Money;
      net_monthly_income: Money;
    };
  };
}

export interface RoleChangedActivity {
  author?: Author;
  activity_type: "role_changed";
  created_at: string;
  updated_at?: string;
  details: {
    contact: Contact;
    previous_role: ApplicantType;
    new_role: ApplicantType;
  };
}

interface EmailChangedActivity {
  author?: Author;
  activity_type: "email_changed";
  created_at: string;
  updated_at?: string;
  details: {
    contact: Contact;
    previous_email: string;
    new_email: string;
  };
}

interface NameChangedActivity {
  author?: Author;
  activity_type: "name_changed";
  created_at: string;
  updated_at?: string;
  details: {
    contact: Contact;
    previous_name: {
      first_name: string;
      middle_name?: string;
      last_name: string;
    };
    new_name: {
      first_name: string;
      middle_name?: string;
      last_name: string;
    };
  };
}

export type ApplicantData =
  | {
      first_name: string;
      last_name: string;
      middle_name?: string;
      email?: string;
      role: ROLE;
    }
  | {
      // Can be null after role was changed
      email?: string;
      role: ROLE;
    };

export interface SplitApplicationActivity {
  author?: Author;
  activity_type: "split_application" | "created_from_split";
  created_at: string;
  updated_at?: string;
  details: {
    moving_applicants: ApplicantData[];
    remaining_applicants: ApplicantData[];
    original_application_id: number;
    split_application_id: number;
  };
}

export interface VerificationTemplateOverriddenActivity {
  author?: Author;
  activity_type: "verification_template_overridden";
  created_at: string;
  updated_at?: string;
  details: {
    original_template_id: number;
    original_template_name: string;
    overridden_template_id: number;
    overridden_template_name: string;
  };
}

export interface VerificationTemplateChangedActivity {
  author?: Author;
  activity_type: "verification_template_changed";
  created_at: string;
  updated_at?: string;
  details: {
    old_template_id: number;
    old_template_name: string;
    new_template_id: number;
    new_template_name: string;
  };
}

export interface TemplateRouteAnsweredActivity {
  author?: Author;
  activity_type: "template_route_answered";
  created_at: string;
  updated_at?: string;
  details: {
    question: string;
    route_key: string;
    route_label: string;
    template_id: number;
    template_name: string;
    target_template_id: number | null;
    target_template_name: string | null;
  };
}

export interface ApplicationActivity {
  author?: Author;
  activity_type:
    | "invited"
    | "resent_invite"
    | "submitted"
    | "group_submitted"
    | "decision_made"
    | "decision_email_sent"
    | "credit_report_sent"
    | "auto_approved"
    | "canadian_criminal_report_invited"
    | "canceled"
    | "uncanceled"
    | "invite_canceled"
    | "remove_criminal_report"
    | "request_retry_identity_verification"
    | "cancel_request_retry_identity_verification"
    | "complete_request_retry_identity_verification"
    | "identity_verification_rerun_requested"
    | "identity_verification_rerun_completed"
    | "identity_verification_rerun_canceled"
    | "identity_verification_bypass_requested"
    | "identity_verification_bypass_approved"
    | "identity_verification_bypass_declined"
    | "failed_identity_verification_manual_override"
    | "failed_warnings_report_manual_override"
    | "landlord_survey_sent"
    | "landlord_survey_manual_resent"
    | "landlord_survey_first_nudge"
    | "landlord_survey_second_nudge"
    | "landlord_survey_final_nudge"
    | "requested_pet_screening"
    | "placed_on_hold"
    | "resumed"
    | "push_applicants_to_property_management_system"
    | "appfolio_guest_card_created"
    | "appfolio_guest_card_already_exists"
    | "appfolio_guest_card_updated"
    | "compliance_report_sent";

  details: {
    invitee_role: ApplicantType;
    email: string;
    status: DecisionStatus;
    application_fee?: Money;
    auto_canceled?: boolean;
    contact?: Contact;
    repeat_application?: RepeatApplication;
    applicant_id?: number;
    offense_description?: string;
    applicant_role?: ApplicantType;
    send_applicants_to_pms?: boolean;
    pms_push_status?: "success" | "failed";
    application_external_id?: string;
    error_message?: string;
    guest_card_status?: "success" | "failed";
    guest_card_name?: string;
    guest_card_external_id?: string;
    guest_card_url?: string;
    message?: string;
    note?: string | null;
    // Why a failed warnings report was overridden.
    reason?: string;
    rental_verification_survey?: {
      landlord_survey_id: number;
      landlord_name: string;
      applicant_name: string;
      landlord_email: string;
      nudge_times: number;
      old_landlord_name: string | null;
      old_landlord_email: string | null;
      sender?: {
        name: string;
        role: ROLE;
      };
    };
  };
  created_at: string;
  updated_at?: string;
}

export interface ItemizedReceiptSentActivity {
  author?: Author;
  activity_type: "itemized_receipt_sent";
  details: {
    contact?: Contact;
  };
  created_at: string;
  updated_at?: string;
}

type PremiumLandlordDetails = {
  name?: string;
  email?: string;
  phone?: string;
};

type PremiumRentalVerificationSurveyBase = {
  author?: Author;
  created_at: string;
  updated_at?: string;
};

export type PremiumRentalVerificationSurveyActivity =
  | (PremiumRentalVerificationSurveyBase & {
      activity_type:
        | "premium_rental_history_survey_sent"
        | "premium_rental_history_survey_resent"
        | "premium_rental_history_survey_manually_sent";
      details: {
        contact?: Contact;
        landlord_details?: PremiumLandlordDetails;
        sent_at?: string;
        counter?: number;
        max_counter?: number;
      };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "premium_rental_history_survey_landlord_details_updated";
      details: {
        original_landlord_details?: PremiumLandlordDetails;
        updated_landlord_details?: PremiumLandlordDetails;
        reason?: string;
      };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "premium_rental_history_survey_log_outreach";
      // Backend also sends `contact` as a free-form string (channel/handle of
      // the outreach). It's omitted from the type to avoid widening the shared
      // `contact` field used by other activity variants — the view reads it
      // via a localized cast.
      details: { note?: string };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "premium_rental_history_survey_case_closed";
      details: { reason?: string; note?: string };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "premium_rental_history_survey_skipped";
      details: { reason?: string | null };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "premium_rental_history_survey_declined";
      details: {
        decline_reason?: string;
        landlord_email?: string;
        landlord_name?: string;
      };
    });

/**
 * BSCPF-6584. Employer verification runs on the same survey engine, but its timeline
 * entries carry the supervisor instead of a landlord, so they are their own union
 * rather than extra members of the rental-history one.
 */
export type EmployerVerificationDetails = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  company_name?: string;
};

export type EmployerVerificationSurveyActivity =
  | (PremiumRentalVerificationSurveyBase & {
      activity_type:
        | "employment_verification_survey_sent"
        | "employment_verification_survey_resent";
      details: {
        employer_details?: EmployerVerificationDetails;
        sent_at?: string;
      };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "employment_verification_supervisor_details_updated";
      details: {
        original_employer_details?: EmployerVerificationDetails;
        updated_employer_details?: EmployerVerificationDetails;
      };
    })
  | (PremiumRentalVerificationSurveyBase & {
      activity_type: "employment_verification_survey_declined";
      details: {
        decline_reason?: string;
        decline_reason_details?: string;
        employer_email?: string;
        employer_phone?: string;
      };
    });

export interface IncomeItemsAssignedActivity {
  author?: Author;
  activity_type: "income_items_assigned";
  details: {
    applicant_name?: string;
    contact?: Contact;
    income?: {
      additional_data?: {
        company_name?: string;
        job_title?: string;
        supervisor: {
          email?: string;
          first_name?: string;
          last_name?: string;
          phone?: string;
        };
      };
      job_title?: string;
      company_name?: string;
      name?: string;
      paid_from?: string;
      description?: string;
    };
    items_ids?: number[];
  };
  created_at: string;
  updated_at?: string;
}

export interface AdjustedApplicationRentActivity {
  author?: Author;
  activity_type:
    | "application_rent_adjusted"
    | "application_rent_automatically_adjusted";

  details: {
    contact?: {
      email: string;
      last_name: string;
      first_name: string;
      middle_name: string;
      role?: ApplicantType;
    };
    new_rent_amounts?: {
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      rent_amount?: Money;
      housing_assistance_voucher_amount?: Money;
      pet_rent_amount?: Money;
      criteria_rent_type?: CriteriaRentType;
    };
    old_rent_amounts?: {
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      rent_amount?: Money;
      housing_assistance_voucher_amount?: Money;
      pet_rent_amount?: Money;
      criteria_rent_type?: CriteriaRentType;
    };
  };
  created_at: string;
  updated_at?: string;
}

export interface AdjustedApplicantFeeActivity {
  author?: Author;
  activity_type: "applicant_pet_rent_adjusted";
  details: {
    contact?: {
      email: string;
      last_name: string;
      first_name: string;
      middle_name: string;
      role?: ApplicantType;
    };
    applicant_name?: string;
    new_amount?: Money;
    old_amount?: Money;
  };
  created_at: string;
  updated_at?: string;
}

export interface ApplicationMergedActivity {
  author?: Author;
  activity_type: "application_merged";
  created_at: string;
  updated_at?: string;
  details: {
    merged_at: string;
    merged_application_ids: string[];
  };
}

export interface ApplicationMovedActivity {
  author?: Author;
  activity_type: "application_moved_to_new_unit";
  created_at: string;
  updated_at?: string;
  details: {
    property_name?: string;
    community_name?: string;
    unit_address?: string;
    previous_property_name?: string;
    previous_community_name?: string;
    previous_unit_address?: string;
  };
}

export interface ApplicationAssignedActivity {
  author?: Author;
  activity_type: "application_assigned";
  created_at: string;
  updated_at?: string;
  details: {
    old_assignees?: { id: number; name: string }[];
    new_assignees?: { id: number; name: string }[];
    property_name?: string;
    unit_name?: string;
    application_portal_link?: string;
  };
}

type ActivityStage = { id: number; name: string; color: string };

export interface ApplicationStageChangedActivity {
  author?: Author;
  activity_type: "stage_changed_manual" | "stage_changed_auto";
  created_at: string;
  updated_at?: string;
  details: {
    from_stage?: ActivityStage | null;
    to_stage?: ActivityStage | null;
    trigger?: "status_changed" | "stage_deleted" | "merged" | string;
  };
}

export interface DesiredMoveInDateUpdatedActivity {
  author?: Author;
  activity_type: "desired_move_in_date_updated";
  created_at: string;
  updated_at?: string;
  details: {
    previous_desired_move_in_date: string | null;
    desired_move_in_date: string;
  };
}

export interface DesiredLeaseTermUpdatedActivity {
  author?: Author;
  activity_type: "desired_lease_term_updated";
  created_at: string;
  updated_at?: string;
  details: {
    previous_desired_lease_term: number | null;
    desired_lease_term: number;
  };
}

export interface ScreeningStatusChangedActivity {
  author?: Author;
  activity_type: "screening_status_changed";
  created_at: string;
  updated_at?: string;
  details: {
    from?: ScreeningStatus | null;
    to?: ScreeningStatus | null;
    requests?: OpenReverificationRequest[];
  };
}

export interface PrimaryApplicantChangedActivity {
  author?: Author;
  activity_type: "primary_applicant_changed";
  created_at: string;
  updated_at?: string;
  details: {
    previous_primary: {
      id: number;
      first_name: string;
      last_name: string;
      role: ApplicantType;
    } | null;
    new_primary: {
      id: number;
      first_name: string;
      last_name: string;
      role: ApplicantType;
    };
  };
}

export interface WorkflowMessageSentActivity {
  author?: Author;
  activity_type: "workflow_sms_sent" | "workflow_email_sent";
  created_at: string;
  updated_at?: string;
  details: {
    template_id?: number;
    template_name?: string;
    email_template_id?: number;
    email_template_name?: string;
    sent_count?: number;
    failed_count?: number;
    skipped_count?: number;
    sent_phones?: string[];
    sent_to?: string[];
  };
}

export interface Author {
  id: number;
  name: string;
  email: string;
  author_role?: ROLE | ApplicantType | typeof AGENT_AUTHOR_ROLE;
}

export type DecisionStatus =
  | ApplicationStatus.Approved
  | ApplicationStatus.Rejected
  | ApplicationStatus.ConditionalApproved
  | string;

export interface FeeRefundedActivity {
  author?: Author;
  activity_type: "fee_refunded";
  created_at?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    fee_type: "application_fee" | "holding_fee" | "administration_fee";
    fee_description: string;
    original_amount: Money;
    refunded_amount: Money;
    refunded_to?: string;
    refunded_to_full_name?: string;
  };
}

export interface ReportRerunActivity {
  author?: Author;
  // Add other report rerun activity_types here when needed
  activity_type: "credit_report_rerun_requested";
  created_at?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    previous_verification_id: number;
    latest_verification_id: number;
  };
}

export interface CriminalStatewideSearchActivity {
  author?: Author;
  activity_type:
    | "criminal_statewide_search_requested"
    | "criminal_statewide_search_received";
  created_at?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    state_code: string;
  };
}

interface CombinedIncomeCommonActivity {
  author?: Author;
  created_at?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    canceled_verification_id?: number;
    restored_verification_id?: number;
    rerun_version?: number;
  };
}
export interface CombinedIncomeRerunRequestedActivity extends CombinedIncomeCommonActivity {
  activity_type: "combined_income_rerun_requested";
}
export interface CombinedIncomeRerunCanceledActivity extends CombinedIncomeCommonActivity {
  activity_type: "combined_income_rerun_canceled";
}

export interface CombinedIncomeRerunCompletedActivity extends CombinedIncomeCommonActivity {
  activity_type: "combined_income_rerun_completed";
}

export interface NewIncomeRequestCreatedActivity extends CombinedIncomeCommonActivity {
  activity_type: "new_income_request_created";
}

export interface NewIncomeRequestCanceledActivity extends CombinedIncomeCommonActivity {
  activity_type: "new_income_request_canceled";
}

export interface NewIncomeRequestCompletedActivity extends CombinedIncomeCommonActivity {
  activity_type: "new_income_request_completed";
}

export interface AgentEscalationDetectedActivity {
  author?: Author;
  activity_type: "agent_escalation_detected";
  created_at: string;
  updated_at?: string;
  details: {
    agent_type: string;
    sentiment_summary: string;
    original_message?: string;
    applicant_name?: string;
    applicant_role?: ApplicantType;
    property_name?: string;
    unit_name?: string;
    alert_id?: number;
  };
}

export interface AgentActionTakenActivity {
  author?: Author;
  activity_type: "agent_action_taken";
  created_at: string;
  updated_at?: string;
  details: {
    agent_type: string;
    action_description: string;
    sentiment_summary?: string;
    original_message?: string;
    applicant_name?: string;
    applicant_role?: ApplicantType;
    property_name?: string;
    unit_name?: string;
    alert_id?: number;
  };
}

export interface AgentEscalatedToSupportActivity {
  author?: Author;
  activity_type: "agent_escalated_to_support";
  created_at: string;
  updated_at?: string;
  details: {
    agent_type: string;
    action_description: string;
    original_message?: string;
    applicant_name?: string;
    applicant_role?: ApplicantType;
    property_name?: string;
    unit_name?: string;
    alert_id?: number;
  };
}

export interface AgentEscalationReviewedActivity {
  author?: Author;
  activity_type: "agent_escalation_reviewed";
  created_at: string;
  updated_at?: string;
  details: {
    agent_type: string;
    reason: string;
    original_message?: string;
    applicant_name?: string;
    applicant_role?: ApplicantType;
    property_name?: string;
    unit_name?: string;
    alert_id?: number;
  };
}

export interface UnderwritingAgentCheckCompletedActivity {
  author?: Author;
  activity_type: "underwriting_agent_check_completed";
  created_at: string;
  updated_at?: string;
  details: {
    check_id: number;
    report_key: UnderwritingReportKey;
    agent_type: string;
    applicant_id: number;
    applicant_name?: string;
    applicant_role?: ApplicantType;
    /** What the agent flagged when the check finished; empty when it flagged nothing. */
    findings?: { id: number; finding: string; reviewed_at?: string | null }[];
  };
}

export interface HousingAssistanceAdjustedActivity {
  author?: Author;
  activity_type: "applicant_housing_assistance_adjusted";
  created_at?: string;
  updated_at?: string;
  details: {
    applicant_name: string;
    old_amount: Money;
    new_amount: Money;
  };
}

export interface UndoDecisionEmailSentActivity {
  author?: Author;
  activity_type: "undo_decision_email_sent";
  created_at: string;
  updated_at?: string;
  details: {
    contact?: Contact;
  };
}

export interface DecisionUndoneActivity {
  author?: Author;
  activity_type: "undo_decision";
  created_at: string;
  updated_at?: string;
  details: {
    previous_status: string;
  };
}

export interface IncomeOverrideActivity {
  author: Author;
  activity_type: "income_finished_by_override";
  created_at?: string;
  updated_at?: string;
  details: {
    contact: Contact;
    income?: {
      id?: number;
      name?: string;
    };
    prior_status?: string;
  };
}

export interface ManualApplicationStartedActivity {
  author?: Author;
  activity_type: "manual_application_started";
  created_at: string;
  updated_at?: string;
  details: {
    contact: Contact;
    invited_at: string;
  };
}

export interface DocumentRequestedActivity {
  author?: Author;
  activity_type: "partner_requested_additional_document";
  created_at: string;
  updated_at?: string;
  details: {
    note?: string;
    applicant_id?: number;
    contact?: Contact;
  };
}

export interface DocumentRequestCanceledActivity {
  author?: Author;
  activity_type: "partner_canceled_additional_document_request";
  created_at: string;
  updated_at?: string;
  details: {
    note?: string;
    applicant_id?: number;
    contact?: Contact;
  };
}

export interface AdditionalDocumentUploadedActivity {
  author?: Author;
  activity_type: "applicant_uploaded_additional_document";
  created_at: string;
  updated_at?: string;
  details?: {
    applicant_id?: number;
    contact?: Contact;
  };
}

export interface RentalHistoryAddressAddedActivity {
  author?: Author;
  activity_type: "housing_history_added";
  created_at: string;
  updated_at?: string;
  details?: {
    contact?: Contact;
  };
}

interface ManualIncomeActivity {
  author: Author;
  details: {
    income: {
      id: number;
      name: string;
    };
    contact: Contact;
  };
  created_at: string;
  updated_at?: string;
}

export interface IncomeAddedManuallyActivity extends ManualIncomeActivity {
  activity_type: "income_added_manually";
}
export interface CancelManuallyAddedIncomeActivity extends ManualIncomeActivity {
  activity_type: "manual_income_canceled";
}

export const LIVE_VIEW_ACTIVITY_TYPES = [
  "live_view_requested",
  "live_view_started",
  "live_view_declined",
  "live_view_expired",
  "live_view_ended"
] as const;

/**
 * BoomView: a viewer asked to watch the applicant's screen, and what came of
 * it. The author is the partner user, or `{ name: "Boom" }` for Boom staff.
 */
export interface LiveViewActivity {
  author?: Pick<Author, "name"> & Partial<Omit<Author, "name">>;
  activity_type: (typeof LIVE_VIEW_ACTIVITY_TYPES)[number];
  created_at: string;
  updated_at?: string;
  details?: {
    session_id?: string;
    applicant_id?: number;
    applicant_name?: string;
    viewer_type?: "partner_user" | "admin";
    /** Always "ask": the applicant accepts every session. */
    consent_mode?: "ask";
    /** On `live_view_ended`: from the applicant accepting; 0 if never started. */
    duration_seconds?: number;
  };
}
